/**
 * echRequired — the required-blank detector behind the Studio's Done gate
 * (Jack, 2026-08-21: divisions "might leave certain boxes blank, especially
 * details related to the listing or featured home like url link or hero image
 * url" — mocked in ech/mockups/REQUIRED-GATE-MOCKUP-20260821.html, locked to
 * the solid-cherry treatment).
 *
 * THE CONTRACT, per Jack's rulings that day:
 *   · GATE, don't trap — free navigation everywhere; only Done (and the form's
 *     submit belt) counts the blanks, with click-to-jump. Never a per-block lock.
 *   · LISTING DATA AUTO-RESOLVES, so it never gates: the listing table's rows
 *     fill from live inventory in Dynamic, and a manually-blank row is DROPPED
 *     by the renderer — a blank there is safe by construction, so `home1..10*`,
 *     `collection*Name` and `viewLabel` are exempt.
 *   · A BUTTON URL NEVER AUTO-RESOLVES: left on the shipped bare "https://" it
 *     is flagged, because a dead button ships a broken email.
 *
 * WHAT COUNTS AS BLANK:
 *   · url fields — anything that isn't a real destination: empty, whitespace,
 *     or the shipped bare "https://" (no host). Keys starting with `img` are
 *     exempt: an image's *link* is optional; its picture is not.
 *   · image fields — EMPTY, or holding a Bynder `/asset/<id>/thumbnail/` preview
 *     URL that no email client can load. A photo that renders — including the
 *     template's own shipped art — does NOT gate (2026-08-26, see `imageBlank`).
 */
import { echMeta, echNameOf } from "./echRegistry";
import { fieldRequired, type GuardrailField } from "./guardrails";
import { echDynFor, echFeatOf, socIsOff, type DynBlock } from "./echDyn";
import type { AnyBlockInstance } from "../email/document";
import { CU_BTN2, CU_LABEL2, CU_URL2 } from "./echContactChoices";

export interface RequiredBlank {
  /** block instance id — what the jump selects */
  id: string;
  type: string;
  /** the block's human label ("Contact Us") */
  block: string;
  /** the field key ("btn1Url") — what the panel highlights */
  k: string;
  /** the field's human label ("Button 1 URL") */
  field: string;
  /**
   * `"data"` (2026-09-09) is a DE-fed fault rather than something the requester left
   * blank — a division with no row, a mandatory column that is empty, or a fed value
   * that fails its pattern. Produced by `echDataGate.dataGateBlanks`, merged into the
   * same list so the Done gate, the submit belt and the Inspector cannot disagree.
   * On a `"data"` row `field` already holds the WHOLE sentence (Layne's copy, from the
   * block's `dataSource.messages`), not a field label.
   */
  kind: "url" | "image" | "spec" | "data";
}

const LISTING_EXEMPT = /^(home\d+|collection\d+Name$|viewLabel$)/;
const REAL_URL = /^https?:\/\/[^\s/]+\./i;

export function urlBlank(v: string): boolean {
  return !REAL_URL.test(v.trim());
}

/**
 * ── A LINK THAT LOOKS FINE AND ISN'T (2026-08-27) ──────────────────────────────
 *
 * OCH, via Layne's Review Board: "no validation on URLs, could include commas & pass."
 * Exactly right, and `REAL_URL` above is why: it is anchored at the START only, so
 * `https://lennar.com` is all it ever looks at. Everything after the host went
 * unexamined — a comma, a space, a second URL pasted on the end — and the email
 * shipped with an href a client cannot resolve. A dead CTA is the most expensive
 * defect this form can produce, which is why this GATES rather than warns.
 *
 * Returns a short reason, or "" when the value is fine. Deliberately narrow: it flags
 * only breakage that is certain, so it can gate without ever being the thing standing
 * between a division and a legitimate send.
 *
 * NOT flagged, on purpose: a very long URL, a tracking query string, a non-Lennar
 * domain (Eventbrite and Zoom links are real), a `mailto:`/`tel:` scheme, and a
 * root-relative path — every one of those is a link someone means.
 */
export function urlProblem(v: unknown): string {
  const t = String(v == null ? "" : v).trim();
  if (!t) return "";                                  /* blank is urlBlank's business */
  if (/^(%%|\{\{|<)/.test(t)) return "";                /* AMPscript / merge token — hands off */
  if (!/^https?:/i.test(t)) return "";                /* mailto:, tel:, /relative — not ours */
  /* ── A BARE SCHEME IS BLANK, NOT BROKEN (2026-08-27) ────────────────────────────
     Every url box now OPENS at "https://" (Jack: "hard code https:// into every url"),
     so the untouched state reaches this function on every render. It is `urlBlank`'s
     business — the Done gate already flags it as a dead button — and reporting it here
     too would put a red "no real domain" line under every url field in the Studio
     before anyone typed a character. */
  if (/^https?:\/*$/i.test(t)) return "";
  if (/\s/.test(t)) return "has a space in it";
  if (/https?:\/\/[^\s]*https?:\/\//i.test(t)) return "looks like two links in one box";
  if (t.indexOf(",") > -1) return "has a comma in it";
  if (/[<>"'`\\{}|^]/.test(t)) return "has a character a link can't contain";
  if (/[.,;:]$/.test(t)) return "ends with a stray " + (t.slice(-1) === "." ? "period" : "punctuation mark");
  if (!/^https?:\/\/[^/?#]*\.[a-z]{2,}(?=$|[/?#:])/i.test(t)) return "has no real domain in it";
  return "";
}

/**
 * ── A BYNDER ADDRESS IS A PHOTO, NOT A PLACE TO SEND SOMEONE (2026-09-24, Justin
 * Bradford, FNO 68759) ─────────────────────────────────────────────────────────────
 * The Main Image's click-through on 68759 was `lennar.bynder.com/share/…`, pasted into
 * the image link box, so every reader who clicked the photo would land in Lennar's
 * internal photo library. Layne's `imgUrl` guardrail already declares a lennar.com-only
 * pattern, but patterns are WARNINGS by design (guardrails.ts: an over-strict pattern
 * must never stop a send), so it went through.
 *
 * This one is certain rather than stylistic: no email button or image link should ever
 * send a reader to bynder.com (media pages, share pages and transform URLs are all
 * the library, not a destination). Photos are unaffected: image fields are typed
 * `image`, never `url`, and a Bynder transform there is exactly right.
 *
 * Kept OUT of `urlProblem` on purpose: that function also checks the form's specialty
 * logo link, which is a reference to a file, not a click destination.
 */
export function bynderLink(v: unknown): boolean {
  const m = String(v == null ? "" : v).trim().match(/^https?:\/\/([^/?#:\s]+)/i);
  return !!m && /(^|\.)bynder\.com$/i.test(m[1]);
}

/** everything that makes a click destination unusable: a broken link, or a Bynder one */
export function linkProblem(v: unknown): string {
  return urlProblem(v) || (bynderLink(v) ? "is a Bynder photo link, not a web page" : "");
}
/**
 * A BYNDER PREVIEW URL IS NOT AN IMAGE (2026-08-21).
 *
 * `lennar.bynder.com/asset/<id>/thumbnail/...` is the picker's own grid preview. An
 * email client loads it from bynder.com and gets nothing, so a slot holding one is
 * effectively empty — and it used to PASS this gate, which is how a hero could be
 * missing with nothing flagged. The picker now refuses to store one; this catches
 * the builds that already did.
 */
const BYNDER_PREVIEW_RE = /bynder\.com\/asset\/[^/]+\/(thumbnail|preview)\b/i;
/* …but a TRANSFORM url is the real thing (2026-08-22): `bynder.com/transform/<id>/…`
   is Bynder's Dynamic Asset Transformation, which serves the asset at whatever size
   was asked for. Since the worker started returning that, a Bynder pick is a valid
   image and must not read as blank — only the `/asset/<id>/thumbnail/` previews do. */
const BYNDER_TRANSFORM_RE = /bynder\.com\/transform\//i;

/**
 * A PHOTO ON SCREEN IS NOT A MISSING PHOTO (2026-08-26).
 *
 * Jack, on a Coming Soon build where the canvas showed a kitchen and the gate still
 * said "1 required missing · Main Image · Image source": "confused this should not be
 * an issue - remove that."
 *
 * The two tests removed both flagged a slot that HAS a working image in it:
 *   · `t === shippedDefault` — the template's own photo, which renders fine and is
 *     often the right photo for the send.
 *   · `PLACEHOLDER_RE` — a filename containing comingsoon/placeholder/default. On a
 *     Coming Soon template that matches Layne's real hero art.
 * Both came from the 2026-08-21 reading that "a wrong hero is worse than a flagged
 * blank". In practice the requester sees the photo in the canvas while the gate calls
 * it missing, which reads as a broken gate and trains people to ignore it.
 *
 * What still gates, because these genuinely ship a broken email: an EMPTY slot, and a
 * Bynder `/asset/<id>/thumbnail/` preview URL, which no email client can load.
 */
export function imageBlank(v: string): boolean {
  const t = v.trim();
  if (!t) return true;
  return BYNDER_PREVIEW_RE.test(t) && !BYNDER_TRANSFORM_RE.test(t);
}

/**
 * THE LISTING ROW'S SPEC FIELDS, in the order the renderer joins them.
 *
 * A blank in the middle is VISIBLE: the row renders "2,234 /  / 2.5" with a hole
 * where Beds should be (Jack, 2026-08-21: "same if i leave any gaps like a missed
 * bedroom count"). An entirely empty row is fine — the renderer drops it — so what
 * gates is a row that is PARTIALLY filled.
 */
const HOME_SPECS = ["Plan", "SqFt", "Beds", "Baths", "Price"] as const;

/**
 * Is every home row on this block still exactly as it shipped?
 *
 * This decides whether the shipped DEMO rows are exempt. A block nobody has touched
 * yet is showing Layne's sample content ("101 / Plan Name"), and flagging five rows
 * on it is the same noise the amenity picker produced when the gate flagged all
 * eight icons on a fresh block. But the moment ANY row is edited the block is the
 * requester's content, and a leftover demo row shipping a dead bare "https://" is
 * exactly what this gate exists for — which is why the old blanket "value equals
 * the shipped default ⇒ exempt" test was too generous (Jack, 2026-08-21: "why are
 * we not flagging certain blocks like homesite missing url").
 */
function homesPristine(fields: Record<string, unknown>, d: Record<string, unknown>): boolean {
  let sawOne = false;
  for (const k of Object.keys(fields)) {
    if (!/^home\d+/.test(k)) continue;
    sawOne = true;
    const def = String((fields[k] as { default?: string } | undefined)?.default ?? "");
    if (String(d[k] ?? "") !== def) return false;
  }
  return sawOne;
}

export interface RequiredOpts {
  /** the dynamic feed (community pack, /refdata, division content) is still in flight */
  feedPending?: boolean;
}

/* ── A FIELD THE FEED IS ABOUT TO FILL IS NOT MISSING YET (2026-09-10, Jack: "when I
   leave creative studio and return, the tool shows for a few seconds that I need to
   update a few items including hero image, but it goes away and I can leave") ────────
   The hero's link, the CTA hrefs, the Get Directions address all ship blank and are
   written by `echFillAll` once the community pack and /refdata land — a second or two
   after the Studio mounts. A Done click inside that window listed them as required
   blanks, the fill then cleared them and the gate disarmed itself, which reads as a
   gate that cannot make up its mind. While the feed is pending, an UNCLAIMED fed field
   is "not yet answered", not "missing"; once the feed settles, whatever is still blank
   gates exactly as before. A claimed field (the requester typed or cleared it) is theirs
   and always gates. */
function feedWillFill(b: AnyBlockInstance, k: string): boolean {
  const name = echNameOf(String(b.type || ""));
  if (!name) return false;
  const m = b as unknown as DynBlock;
  const map = echDynFor(name, echFeatOf(m));
  return !!(map && map[k]) && !socIsOff(m, k);
}

export function requiredBlanks(blocks: AnyBlockInstance[], opts: RequiredOpts = {}): RequiredBlank[] {
  const out: RequiredBlank[] = [];
  for (const b of blocks || []) {
    /* meta.FULL.fields, not gr.fields: the registry's gr map is cap-filtered and
       deliberately EXCLUDES image/url/number/tel (the 24-character trap) — the
       full declared map with types and shipped defaults lives on `full` */
    const meta = echMeta(b.type);
    const fields = meta && meta.full && meta.full.fields;
    if (!fields) continue;
    const blockLabel = String((meta.gr && meta.gr.label) || b.type);
    const d = (b.data || {}) as Record<string, unknown>;
    /* the amenity section's icons are governed by their OWN picker (minimum-3
       rule with its own refusal toast, defaults drawn from the icon library) —
       the gate flagging all 8 on a fresh block was measured live 2026-08-21 and
       was pure noise. Fields labeled "optional" never gate, anywhere. */
    const amenity = /^amenit/i.test(String((meta.gr && meta.gr.module) || ""));  /* amenities4, amenitiesSection, … */
    for (const k of Object.keys(fields)) {
      if (LISTING_EXEMPT.test(k)) continue;
      if (/optional/i.test(String((fields[k] as { label?: string }).label || ""))) continue;
      if (amenity && (fields[k] as { type?: string }).type === "image") continue;
      const f = fields[k] as GuardrailField;
      const v = String(d[k] ?? "");
      /* ── THE DECLARATION DECIDES, THE TYPE IS ONLY THE FALLBACK (Layne, BLOCKER-
         REQUIRED-BY-TYPE-20260902) ──────────────────────────────────────────────────
         This used to read `f.type` and nothing else: url and image gated, the rest did
         not, and `required: false` / `authorEditable: false` on the field were never
         looked at. Declaring the 17 DE-fed Contact Us fields therefore stopped every
         author submitting — "9 required missing" for photos the division's row fills.
         Same shape as the pattern bug #86 fixed. `fieldRequired` reads the declaration
         first; the type-default arguments below are exactly the old behaviour, so a
         block that declares nothing gates precisely as it did. */
      /* a Bynder address in ANY link box gates, required or not: the image link (`imgUrl`)
         is optional, so the blank check below skips it, but a filled one that sends
         readers to the photo library ships a wrong email (FNO 68759, 2026-09-24) */
      if (f.type === "url" && bynderLink(v)) {
        out.push({ id: b.id, type: b.type, block: blockLabel, k,
          field: String(f.label || k) + " · " + linkProblem(v), kind: "url" });
        continue;
      }
      const typeDefault = f.type === "image" || (f.type === "url" && !/^img/i.test(k));
      if (!fieldRequired(f, typeDefault)) continue;
      if (opts.feedPending && feedWillFill(b, k)) continue;
      if (f.type === "image") {
        if (imageBlank(v)) {
          out.push({ id: b.id, type: b.type, block: blockLabel, k, field: String(f.label || k), kind: "image" });
        }
      } else if (f.type === "url") {
        /* blank, OR filled with something that cannot work (2026-08-27, OCH: "no
           validation on URLs, could include commas & pass"). The reason rides the
           field label after a middot, which is the format the gate popover already
           folds parentheticals into — so a malformed link needs no new UI to be
           explained, and the form's submit belt inherits it for free. */
        const bad = urlProblem(v);
        if (urlBlank(v) || bad) {
          out.push({ id: b.id, type: b.type, block: blockLabel, k,
            field: String(f.label || k) + (bad && !urlBlank(v) ? " · " + bad : ""), kind: "url" });
        }
      } else if (v.trim() === "") {
        /* a text / select / number / tel field reaches here only when it DECLARED
           `required: true` (the type default is false) — Layne's Get Directions URL
           pattern applied to a non-url field. Blank is the only failure a plain field
           has; format is the pattern sweep's business. */
        out.push({ id: b.id, type: b.type, block: blockLabel, k, field: String(f.label || k) + " is blank", kind: "spec" });
      }
    }
    /* ── CONTACT US: A SECOND BUTTON THE REQUESTER ADDED NEEDS A LABEL AND A LINK
       (2026-09-25). Until it has both it does not ship (echContactChoices.cuLookup), so
       the gate says what is missing instead of the button quietly not appearing. A link
       typed over the division's own second button is checked the same way. */
    if (/^ECH_ContactUs/i.test(echNameOf(b.type) || "")) {
      const mode = String(d[CU_BTN2] ?? "");
      const added = mode === "add";
      const u2 = mode === "off" ? "" : String(d[CU_URL2] ?? "").trim();
      if (added && !String(d[CU_LABEL2] ?? "").trim()) {
        out.push({ id: b.id, type: b.type, block: blockLabel, k: CU_LABEL2, field: "Second button — label is blank", kind: "spec" });
      }
      const p2 = u2 ? contactLinkProblem(u2) : (added ? "is blank" : "");
      if (p2) out.push({ id: b.id, type: b.type, block: blockLabel, k: CU_URL2, field: "Second button — link " + p2, kind: "url" });
    }
    /* ── A FILLED HOME ROW MUST CARRY A REAL VIEW URL (2026-08-21, Jack: "make
       sure we flag if a homesite doesn't have a link… so every homesite has a
       link"). The blanket home\d+ exemption above stays right for EMPTY rows —
       the renderer drops those — but a row that SHIPS with a dead bare https://
       View Home link is exactly the class this gate exists for. Dynamic fills
       write the feed's real URL, so a fed row passes without special-casing.
       A row still sitting on the block's SHIPPED demo values ("101 / Plan
       Name") stays exempt, same charter as the blanket exemption — it is not
       the requester's content yet. */
    /* ── EVERY ROW THAT WILL RENDER IS CHECKED: its link, and its spec gaps ─────
       Two rules, both from Jack on 2026-08-21. "Make sure we flag if a homesite
       doesn't have a link… so every homesite has a link" — and then, on seeing a
       row render as "2,234 /  / 2.5": "same if i leave any gaps like a missed
       bedroom count".

       The DEMO exemption is now per BLOCK, not per row. While nothing on the block
       has been touched it is Layne's sample content and flagging it is noise; once
       any row is edited the whole block is the requester's, and a leftover demo row
       with a dead bare "https://" is precisely what this gate is for. The old
       per-row test exempted a demo row forever, which is why Jack's 101 / 201 / 301
       rows sailed through with no link. */
    const pristine = homesPristine(fields, d);
    for (const k of Object.keys(fields)) {
      const rr = /^home(\d+)Url$/.exec(k);
      if (!rr) continue;
      const n = +rr[1];
      const hs = String(d["home" + n + "Homesite"] ?? "").trim();
      const pl = String(d["home" + n + "Plan"] ?? "").trim();
      if (!hs && !pl) continue;                            /* empty row — dropped at render */
      if (pristine) continue;                              /* untouched block — Layne's sample */
      const hv = String(d[k] ?? "");
      const hbad = linkProblem(hv);
      if (urlBlank(hv) || hbad) {
        out.push({ id: b.id, type: b.type, block: blockLabel, k,
          field: "Home " + n + " — View Home URL" + (hbad && !urlBlank(hv) ? " · " + hbad : ""), kind: "url" });
      }
      /* ── COLUMN 1 IS TWO CELLS, AND BOTH SHIP (2026-09-15) ─────────────────────────
         CHB's table rendered "/ Napa" down the first column: plan names typed, homesites
         left blank. `used` is Homesite OR Plan (so the row ships), and the spec sweep
         below never looked at Homesite — so the leading slash was the one gap this gate
         let through. Layne's rule is explicit ("Column 1 always reads homesite, space,
         slash, space, plan name … Do not leave a cell blank"), and on a plan-level fill
         the slot carries the plan number, so a fed row never trips this. */
      if (pl && !hs && ("home" + n + "Homesite") in fields) {
        out.push({ id: b.id, type: b.type, block: blockLabel, k: "home" + n + "Homesite",
          field: "Home " + n + " — Homesite is blank", kind: "spec" });
      }
      /* A PARTIALLY FILLED ROW. Only flag a blank spec when SIBLINGS are filled:
         that is the state that renders a visible hole. A row with no specs at all is
         the requester choosing not to list them, and the renderer copes. */
      const present = HOME_SPECS.filter((sp) => String(d["home" + n + sp] ?? "").trim() !== "");
      if (!present.length) continue;
      HOME_SPECS.forEach((sp) => {
        const key = "home" + n + sp;
        if (!(key in fields)) return;                      /* this block does not declare it */
        if (String(d[key] ?? "").trim() !== "") return;
        out.push({
          id: b.id, type: b.type, block: blockLabel, k: key,
          field: "Home " + n + " — " + (sp === "SqFt" ? "Sq ft" : sp) + " is blank",
          kind: "spec",
        });
      });
    }
  }
  return out;
}

/**
 * What is wrong with a Contact Us button link, or "" — a web page, a `tel:` number or a
 * `mailto:` address are all real answers here (NEV's buttons are the last two).
 */
export function contactLinkProblem(v: string): string {
  const t = String(v || "").trim();
  if (!t) return "is blank";
  if (/^tel:/i.test(t)) return /^tel:\+?[\d\s().-]{7,}$/i.test(t) ? "" : "is not a phone number";
  if (/^mailto:/i.test(t)) return /^mailto:[^\s@,;]+@[^\s@,;]+\.[^\s@,;?]+(\?.*)?$/i.test(t) ? "" : "is not an email address";
  const w = /^www\./i.test(t) ? "https://" + t : t;
  if (urlBlank(w)) return "is not a web address";
  return urlProblem(w);
}
